/**
 * Same-origin `/api`-calling sibling of `backendChatProvider.ts` /
 * `backendHealthProvider.ts` (connect-with-claude-8bn): calls this app's own
 * backend (`GET /api/models`) over `fetch`, supplying the caller's key via
 * the same `x-anthropic-api-key` header — the browser never talks to
 * Anthropic directly.
 *
 * Never falls back internally on failure — `ConfigureView.vue` owns the
 * CURATED_MODELS fallback decision.
 */
const API_KEY_HEADER = 'x-anthropic-api-key';

export interface ModelOption {
  id: string;
  label: string;
}

async function errorFromResponse(res: Response): Promise<Error> {
  try {
    const body = (await res.json()) as { error?: string };
    return new Error(body.error ?? 'Backend unavailable.');
  } catch {
    return new Error('Backend unavailable.');
  }
}

export async function fetchModels(
  apiKey: string,
  baseUrl?: string,
  fetchImpl: typeof fetch = fetch,
): Promise<ModelOption[]> {
  const base = baseUrl ?? '';
  let res: Response;
  try {
    res = await fetchImpl(`${base}/api/models`, {
      headers: { [API_KEY_HEADER]: apiKey },
    });
  } catch {
    throw new Error('Backend unavailable.');
  }
  if (!res.ok) {
    throw await errorFromResponse(res.clone());
  }
  try {
    const body = (await res.json()) as { models: ModelOption[] };
    return body.models;
  } catch {
    throw new Error('Backend unavailable.');
  }
}
